import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';

import { S3ClientService, timestamp } from '../../tools/helpers';
import { queryKeys } from './constants';

export const getS3FileURLQueryConfig = ({
  bucket,
  fileName,
}: {
  fileName: string;
  bucket: string;
}) => ({
  queryFn: async () => {
    return await S3ClientService.getFileS3BucketUrl(fileName, bucket);
  },
  queryKey: [queryKeys.s3.root, bucket, fileName],
});

export const useS3FileUrlInlineQuery = () => {
  const queryClient = useQueryClient();
  return useCallback(
    async (arg: { fileName: string; bucket: string }) => {
      const query = getS3FileURLQueryConfig(arg);
      const cache = queryClient.getQueryData<string>(query.queryKey);
      if (!cache) {
        const url = await query.queryFn();
        queryClient.setQueryData(query.queryKey, url);
        return url;
      }
      return cache;
    },
    [queryClient],
  );
};

export const useS3FileUrlQuery = ({
  filter,
  enabled,
}: {
  filter: { fileName: string; bucket: string };
  enabled?: boolean;
}) => {
  return useQuery({ ...getS3FileURLQueryConfig(filter), enabled });
};

export const useS3FileObjectUrlQUery = ({
  filter,
  enabled,
}: {
  filter: { bucket: string; fileName: string; mimeType: string };
  enabled?: boolean;
}) => {
  const urlQuery = useS3FileUrlQuery({ filter, enabled });

  const result = useQuery({
    queryKey: [queryKeys.s3.root, 'objectUrl', filter.mimeType, urlQuery.data],
    queryFn: async () => {
      const response = await fetch(urlQuery.data ?? '');
      const data = await response.arrayBuffer();
      const blob = new Blob([data], { type: filter.mimeType || 'application/octet-stream' });
      const objectUrl = URL.createObjectURL(blob);
      return objectUrl;
    },
    enabled: urlQuery.isSuccess && urlQuery.data.length > 0 && enabled,
  });

  useEffect(() => {
    return () => {
      if (result.isSuccess) {
        URL.revokeObjectURL(result.data);
      }
    };
  }, [result.data, result.isSuccess]);

  return result;
};

export const getS3FileQueryConfig = ({
  bucket,
  fileName,
}: {
  bucket: string;
  fileName: string;
}) => ({
  queryFn: async () => {
    return await S3ClientService.downloadFile({ bucket, fileName });
  },
  queryKey: [queryKeys.s3.root, queryKeys.s3.file, bucket, fileName],
});

// New multipart upload hooks

export const useMultipartUploadStartMutation = () => {
  return useMutation({
    mutationFn: async (params: { bucket: string; key: string; mimeType: string }) => {
      return await S3ClientService.startMultipartUpload({
        uploadId: '',
        finalBucket: params.bucket,
        key: params.key,
        mimeType: params.mimeType,
        parts: [],
        ownerId: 0,
        dateCreated: timestamp(),
      });
    },
    mutationKey: [queryKeys.s3.multipartUploadStart],
  });
};

export const useMultipartUploadUrlMutation = () => {
  return useMutation({
    mutationFn: async (params: { uploadId: string; key: string; partNumber: number }) => {
      return await S3ClientService.getMultipartUploadUrl({
        uploadId: params.uploadId,
        uniqueUrl: '',
        key: params.key,
        partNumber: params.partNumber,
      });
    },
    mutationKey: [queryKeys.s3.multipartUploadUrl],
  });
};

export const useMultipartUploadCompleteMutation = () => {
  return useMutation({
    mutationFn: async (params: {
      uploadId: string;
      bucket: string;
      key: string;
      mimeType: string;
      parts: Array<{ eTag: string; part: number }>;
    }) => {
      return await S3ClientService.endMultipartUpload({
        uploadId: params.uploadId,
        finalBucket: params.bucket,
        key: params.key,
        mimeType: params.mimeType,
        parts: params.parts,
        ownerId: 0,
        dateCreated: timestamp(),
      });
    },
    mutationKey: [queryKeys.s3.multipartUploadComplete],
  });
};

export const useLargeFileUploadMutation = () => {
  return useMutation({
    mutationFn: async (params: {
      file: File;
      bucket: string;
      key: string;
      chunkSize?: number;
      onProgress?: (progress: number) => void;
    }) => {
      return await S3ClientService.uploadLargeFile(params);
    },
    mutationKey: [queryKeys.s3.largeFileUpload],
  });
};

// Utility function to determine if a file should use multipart upload
export const shouldUseMultipartUpload = (file: File, sizeThreshold = 50 * 1024 * 1024): boolean => {
  // Use multipart upload only for files larger than 50MB to avoid range request issues with videos
  // Videos need proper range request support for seeking/playback
  return file.size > sizeThreshold;
};

// Utility function to check if a file is a video
export const isVideoFile = (file: File): boolean => {
  return file.type.startsWith('video/');
};
